import {
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  KeyOutlined,
  LockOutlined,
  PlusOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { Popconfirm, Tag, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { staffApi } from "api/staff.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  INA,
  TableLayout,
  TableLayoutFooter,
  TableContent,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ModalStatus } from "types/modal";
import { QueryParam } from "types/query";
import { Staff } from "types/staff";
import { getTitle } from "utils";
import { $url } from "utils/url";
import { ResetPasswordModal } from "./components/ResetPasswordModal";
import { StaffModal } from "./components/StaffModal";

export const StaffPage = ({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 50,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Staff[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleModal, setVisibleModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<Partial<Staff>>({});
  const [staffIds, setStaffIds] = useState<number[]>([]);
  const [modalStatus, setModalStatus] = useState<ModalStatus>("create");
  const [visibleResetPasswordModal, setVisibleResetPasswordModal] =
    useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [selectedStaffs, setSelectedStaffs] = useState<Staff[]>([]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: any[], records: Staff[]) => {
      setSelectedRowKeys(keys);
      setSelectedStaffs(records);
    },
  };
  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const fetchData = useCallback(async (queryParams: QueryParam) => {
    setLoading(true);
    try {
      const res = await staffApi.findAll(queryParams);
      setData(res.data.data);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDelete = useCallback(
    async (row: Staff) => {
      setLoading(true);
      try {
        await staffApi.delete(row.id);
        await fetchData(query);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, query],
  );

  const handleBlock = useCallback(
    async (staffs: Staff[], block: boolean) => {
      try {
        for (let i = 0; i < staffs.length; i++) {
          const staff = staffs[i];
          await staffApi.block(staff.id, {
            staff: {
              name: staff.name,
              avatar: staff.avatar,
              phone: staff.phone,
              email: staff.email,
              isBlocked: block,
            },
            roleId: staff.role.id,
          });
        }
        if (block) {
          message.success("Khóa người dùng thành công");
        } else if (block === false) {
          message.success("Mở khóa người dùng thành công");
        }
      } finally {
        setLoading(false);
        await fetchData(query);
        setSelectedStaffs([]);
        setSelectedRowKeys([]);
      }
    },
    [fetchData, query],
  );

  const debounceSearch = useCallback(
    debounce(
      (keyword: string) =>
        setQuery({ ...query, search: keyword, page: 1, limit: 20 }),
      300,
    ),
    [setQuery, query],
  );

  useEffect(() => {
    fetchData(query);
  }, [fetchData, query]);

  const columns = useMemo<TableColumnsType<Staff>>(
    () => [
      {
        title: "Username",
        dataIndex: "username",
        key: "username",
        width: 250,
        render: (_, record) => (
          <div className="space-y-1 min-w-0">
            <INA
              image={record.avatar ? $url(record.avatar) : undefined}
              name={record.username || "--"}
              additional={record.name || "--"}
            />
            {record.isBlocked && <Tag color="red">Bị khóa</Tag>}
          </div>
        ),
      },
      {
        title: "Họ tên",
        dataIndex: "name",
        key: "name",
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Số điện thoại",
        dataIndex: "phone",
        key: "phone",
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Quyền",
        key: "role",
        width: 180,
        render: (_, record) => <TableContent>{record.role?.name || "--"}</TableContent>,
      },
      {
        title: "Thao tác",
        key: "action",
        width: 160,
        align: "center",
        render: (_, record) => (
          <div className="flex gap-2 justify-center items-center">
            <Tooltip title="Cập nhật">
              <ActionButton
                aria-label="Cập nhật"
                onClick={() => {
                  setSelectedStaff(record);
                  setVisibleModal(true);
                  setModalStatus("update");
                }}
              >
                <EditOutlined style={{ color: "#6c84e5" }} />
              </ActionButton>
            </Tooltip>
            <Tooltip title="Xóa">
              <Popconfirm
                cancelText="Không"
                okText="Xóa"
                onConfirm={() => handleDelete(record)}
                title="Xác nhận xóa?"
              >
                <ActionButton aria-label="Xóa">
                  <DeleteOutlined style={{ color: "#ef4444" }} />
                </ActionButton>
              </Popconfirm>
            </Tooltip>
            <Tooltip title={record.isBlocked ? "Mở khóa" : "Khóa"}>
              <Popconfirm
                cancelText="Không"
                okText={record.isBlocked ? "Bỏ" : "Khóa"}
                onConfirm={() => {
                  handleBlock([record], !record.isBlocked);
                }}
                title={`Xác nhận ${record.isBlocked ? "bỏ" : ""} khóa?`}
              >
                <ActionButton aria-label={record.isBlocked ? "Mở khóa" : "Khóa"}>
                  {record.isBlocked ? (
                    <UnlockOutlined style={{ color: "#22c55e" }} />
                  ) : (
                    <LockOutlined style={{ color: "#f59e0b" }} />
                  )}
                </ActionButton>
              </Popconfirm>
            </Tooltip>
            <Tooltip title="Reset mật khẩu">
              <ActionButton
                aria-label="Reset mật khẩu"
                onClick={() => {
                  setSelectedStaff(record);
                  setVisibleResetPasswordModal(true);
                }}
              >
                <KeyOutlined style={{ color: "#2563eb" }} />
              </ActionButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [handleBlock, handleDelete],
  );

  const { showingFrom, showingTo } = getTableShowingRange(
    query.page,
    query.limit,
    total,
  );

  return (
    <div>
      <TableLayout
          filter={
            <AppTableToolbar
              search={{
                value: search,
                placeholder: "Tìm tên, số điện thoại",
                onChange: (value) => {
                  setSearch(value);
                  debounceSearch(value);
                },
                inputProps: {
                  allowClear: {
                    clearIcon: (
                      <CloseCircleFilled
                        onClick={() => {
                          setSearch("");
                          setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                        }}
                      />
                    ),
                  },
                },
              }}
              leftActions={
                <AppButton
                  tone="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setVisibleModal(true);
                    setModalStatus("create");
                    setSelectedStaff({});
                  }}
                >
                  Tạo mới
                </AppButton>
              }
              actions={
                <Popconfirm
                  cancelText="Không"
                  okText="Khóa"
                  onConfirm={() => handleBlock(selectedStaffs, true)}
                  title="Xác nhận khóa?"
                >
                  <AppButton
                    tone="light"
                    icon={<LockOutlined />}
                    disabled={!selectedRowKeys.length}
                    loading={loading}
                  >
                    Khóa
                  </AppButton>
                </Popconfirm>
              }
              filterLabel={null}
            />
          }
          table={
            <AppTable<Staff>
              rowSelection={rowSelection}
              rowKey="id"
              loading={loading}
              pagination={false}
              dataSource={data}
              columns={columns}
              scroll={{ x: 1100 }}
            />
          }
          footer={
            <TableLayoutFooter
              showingFrom={showingFrom}
              showingTo={showingTo}
              total={total}
              currentPage={query.page}
              pageSize={query.limit}
              onChange={({ limit, page }) => {
                setQuery((prev) => ({ ...prev, page, limit }));
              }}
            />
          }
        />
      <ResetPasswordModal
        onSubmitOk={() => {}}
        status={"create"}
        staffId={selectedStaff.id || 0}
        visible={visibleResetPasswordModal}
        onClose={() => {
          setVisibleResetPasswordModal(false);
        }}
      />

      <StaffModal
        onSubmitOk={() => fetchData(query)}
        onClose={() => setVisibleModal(false)}
        visible={visibleModal}
        staff={selectedStaff}
        status={modalStatus}
      />
    </div>
  );
};
